import { ColumnDef } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { useSafeUrlState } from '@mews-framework/utils';
import { PaginationSize, TableFilters, TableState, useTableState } from '@optimus-web/core';
import { createTableStateUrlSchema } from './tableStateUrlSchema';

interface Options<Row> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    columns: ColumnDef<Row, any>[];
    pageSize?: PaginationSize;
    /**
     * Use it to merge additional query params into URL.
     *
     * By default, useUrlSyncedTableState will override any existing query params.
     * This option allows you to keep any additional, table non-related query params in the URL.
     */
    extraQuery?: Record<string, unknown>;
}

export const useUrlSyncedTableState = <Row, Filters extends TableFilters<Row> = TableFilters<Row>>({
    columns,
    pageSize,
    extraQuery,
}: Options<Row>) => {
    const schema = useMemo(() => createTableStateUrlSchema<Row, Filters>(columns), [columns]);
    const [urlState, setUrlState] = useSafeUrlState<Partial<TableState<Row, Filters>>>(schema);
    const pagination = pageSize ? { pagination: { pageSize } } : undefined;
    const [tableState, setTableState] = useTableState<Row, Filters>({
        ...pagination,
        ...(urlState ?? {}),
    });
    const handleStateChange = useCallback(
        (nextState: TableState<Row, Filters>) => {
            const { filters, globalFilters, sort } = nextState;
            setUrlState({ ...extraQuery, filters, globalFilters, sort });
            setTableState(nextState);
        },
        [setTableState, setUrlState, extraQuery]
    );

    return [tableState, handleStateChange] as const;
};
